import React, { useState, useEffect } from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import DashboardRecentActivities from '../../components/dashboard/DashboardRecentActivities';
import { useLazyGetRecentActivitiesQuery } from '../../store/api';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';

/**
 * Full activity log listing under Reports (same API as former dashboard widget).
 */
const RecentActivitiesReport = () => {
  const navigate = useNavigate();
  const [fetchRecentActivities] = useLazyGetRecentActivitiesQuery();

  const [list, setList] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setActivitiesLoading(true);
        const res = await fetchRecentActivities({
          page: pagination.page,
          pageSize: pagination.pageSize,
          search: searchTerm,
        }).unwrap();
        if (!cancelled) {
          setList(extractList(res));
          if (res?.success && res?.data?.pagination) {
            setPagination((prev) => ({ ...prev, total: res.data.pagination?.total || 0 }));
          }
        }
      } catch {
        if (!cancelled) {
          setList([]);
          toast.error('Failed to load activities.');
        }
      } finally {
        if (!cancelled) setActivitiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchRecentActivities, pagination.page, pagination.pageSize, searchTerm]);

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newSize) => {
    setPagination((prev) => ({ ...prev, page: 1, pageSize: newSize }));
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3, width: 1, minWidth: 0, boxSizing: 'border-box' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Recent Activities
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Full activity log with search and pagination. Open a row to view details.
          </Typography>
        </Box>
        <DashboardRecentActivities
          title="Activity log"
          list={list}
          pagination={pagination}
          loading={activitiesLoading}
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          onSearchKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          onClearSearch={handleClearSearch}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onViewLog={(id) => navigate(`/view-log/${id}`)}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Container>
    </PageLayout>
  );
};

export default RecentActivitiesReport;
